import React, { useContext, useEffect, useState } from 'react';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import useFetch from 'use-http';
import { OwnerWhereAttachedDTO } from 'src/models/OwnerWhereAttached';
import Button from '../controls/forms/redesign/FormButton';
import { IoMdDownload } from 'react-icons/io';
import Dialog from 'src/components/common/Dialog.redesign';
import Pager from '../common/table/Pager';
import PagedResultSet from 'src/models/common/PagedResultSet';
import MathUtil from 'src/util/MathUtil';
import LoadingSpinner from '../common/LoadingSpinner';
import './WhereAttachedDialog.scss';

interface IWhereAttachedDialogProps {
    ownerId: number;
}

const PAGE_SIZE = 25;

const WhereAttachedDialog: React.FC<IWhereAttachedDialogProps> = ({ ownerId }) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get: getData, response: jsonResponse } = useFetch();
    const { get: getExcel } = useFetch({
        headers: {
            Accept: 'application/octet-stream',
        },
        responseType: 'blob',
    });

    const [whereAttachedData, setWhereAttachedData] = useState<PagedResultSet<OwnerWhereAttachedDTO>>();
    const [pageNumber, setPageNumber] = useState(1); // Track page state
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        getWhereAttachedData(ownerId, pageNumber);
    }, [ownerId, pageNumber]);

    const getWhereAttachedData = async (id: number, page: number) => {
        const skip = (page - 1) * PAGE_SIZE;
        setIsLoading(true);

        const data = await getData(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/${id}/where-attached?$skip=${skip}&$top=${PAGE_SIZE}`
        );

        if (jsonResponse.ok) {
            setWhereAttachedData(data);
        } else {
            console.log('bad request', jsonResponse);
        }
        setIsLoading(false);
    };

    const downloadWhereAttachedData = async () => {
        try {
            const fileBlob: Blob = await getExcel(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/${ownerId}/where-attached/report`
            );

            if (!fileBlob || fileBlob.type !== 'application/octet-stream') {
                console.log('Unexpected file response:', fileBlob);
                return;
            }

            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `WhereAttached_Owner_${ownerId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log('Download failed:', error);
        }
    };

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) {
            return '—';
        }

        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return '—';
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    return (
        <Dialog title="Where Attached">
            {/* Report button aligned right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: '1rem', marginBottom: '12px' }}>
                <Button
                    reactIcon={IoMdDownload}
                    iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'middle' }}
                    iconHoverColor="blue"
                    title="Download"
                    style={{ height: '3rem', background: 'transparent' }}
                    onClick={downloadWhereAttachedData}
                />
            </div>

            {/* Scrollable container */}
            <div style={{ width: '90vw', height: '50vh', overflow: 'auto' }}>
                <table style={{ minWidth: '1200px', width: '100%', borderCollapse: 'separate', borderSpacing: '0 12px' }}>
                    <thead style={{ textAlign: 'left' }}>
                        <tr>
                            <th style={{ width: '3%' }} className="table-header">Type</th>
                            <th style={{ width: '6%' }} className="table-header">Owner Name</th>
                            <th style={{ width: '6%' }} className="table-header">Global ID</th>
                            <th style={{ width: '12%' }} className="table-header">Horizon Desc</th>
                            <th style={{ width: '7%' }} className="table-header">Sec/Twn/Rng</th>
                            <th style={{ width: '15%' }} className="table-header">Legal Description</th>
                            <th style={{ width: '10%' }} className="table-header">County</th>
                            <th style={{ width: '5%' }} className="table-header">Gross Acres</th>
                            <th style={{ width: '4%' }} className="table-header">Net Acres</th>
                            <th style={{ width: '4%' }} className="table-header">Decimal Interest</th>
                            <th style={{ width: '5%' }} className="table-header">Lease #</th>
                            <th style={{ width: '5%' }} className="table-header">Lease Date</th>
                            <th style={{ width: '5%' }} className="table-header">Effective Dates</th>
                            <th style={{ width: '4%' }} className="table-header">Status</th>
                            <th style={{ width: '9%' }} className="table-header">Buyer</th>
                        </tr>
                    </thead>
                    <tbody style={{ marginTop: '18px' }}>
                        {whereAttachedData?.items.map((item, index) => (
                            <tr
                                key={index}
                                className="table-item-hover"
                                style={{ textAlign: 'left', maxHeight: '2rem', overflow: 'hidden' }}
                            >
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '3%' }}>{item.ownershipType ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '6%' }}>{item.ownerName ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '6%' }}>{item.globalId ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '12%' }}>{item.horizonDescription ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '7%' }}>{item.secTwnRng ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '15%' }}>
                                    <div style={{ maxHeight: '5rem', overflow: 'hidden', paddingBottom: '0.5rem' }} title={item.legalDescription}>{item.legalDescription ?? '—'}</div>
                                </td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '10%' }}>{item.countyStateName ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '5%' }}>{MathUtil.round(item.grossAcres, 7) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '4%' }}>{MathUtil.round(item.netAcres, 7) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '4%' }}>{MathUtil.round(item.decimalInterest, 10) ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '5%' }}>{item.leaseNumber ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '5%' }}>{formatDate(item.leaseDate)}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '5%' }}>{formatDate(item.effectiveDate)}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '4%' }}>{item.leasedStatus ?? '—'}</td>
                                <td style={{ verticalAlign: 'top', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', width: '9%' }}>{item.leaseBuyerName ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {isLoading && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh',
                            width: '100%',
                        }}
                    >
                        <LoadingSpinner />
                    </div>
                )}
            </div>

            {/* Pagination */}
            {whereAttachedData && (
                <Pager
                    pageSize={PAGE_SIZE}
                    pageNumber={whereAttachedData.pageNumber}
                    totalRecords={whereAttachedData.totalRecords}
                    onPageChange={(newPage: number) => setPageNumber(newPage)}
                    currentPageCount={whereAttachedData.items.length}
                />
            )}
        </Dialog>
    );
};

export default WhereAttachedDialog;



// WEBPACK FOOTER //
// ./src/components/dialogs/WhereAttachedDialog.tsx